import { BulbOutlined, CaretRightOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Button, Form, Input, Modal, Tooltip, Typography } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { MultipleUserSearch } from '@/components/multiple-user-search/multiple-user-search.component'
import {
  GetTimelinePostsDocument,
  PostType,
  RequestedPostFragment,
  SectionType,
  useGetGroupQuery,
  useGetViewerQuery,
  usePostCreateMutation,
  usePostUpdateMutation,
} from '@/generated/graphql'
import { emptyHonorState } from '@/modules/post/post-editor/post-editor.state'

import s from '../event-post/event-post-modal.module.less'
import { PostEditorSelectorList } from '../post-editor/post-editor-selector.component'

export const HonorPostModal: React.FC<{ post?: RequestedPostFragment; groupId?: number; sectionId?: SectionType }> = ({
  post,
  children,
  groupId: passedInGroupId,
  sectionId,
  ...props
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const [visible, { toggle }] = useToggle()
  const [groupSelectorVisible, { toggle: toggleGroupSelectorVisible }] = useToggle()
  const [groupId, setGroupId] = React.useState<number | undefined>(post ? post.group?.groupId : passedInGroupId || 1)

  const { data } = useGetViewerQuery()

  const { data: groupData } = useGetGroupQuery({
    fetchPolicy: 'cache-first',
    skip: !groupId,
    variables: {
      groupId: groupId!,
      withAdmins: true,
      pagination: {
        first: 1000,
      },
    },
  })

  const [postCreate, { loading: createLoading }] = usePostCreateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
  })

  const [postUpdate, { loading: updateLoading }] = usePostUpdateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
  })

  React.useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible])

  const initialValues = post
    ? {
        title: post.honor?.title,
        commendation: post.honor?.commendation,
        honoredProjects: post.honor?.honoredProjects,
        honoredIndividuals: post.honor?.honoredIndividuals.map((mention) => mention.user),
        honoredSignificants: post.honor?.honoredSignificants?.map((mention) => mention.user),
      }
    : emptyHonorState

  const viewer = data!.viewer
  const group = groupData?.group
  const showGroupSelector = !post

  const onOK = async () => {
    const values = await form.validateFields()
    const honorInfo = {
      title: values.title,
      commendation: values.commendation,
      honoredProjects: values.honoredProjects,
      honoredIndividuals: values.honoredIndividuals.map((indi: { email: string }) => indi.email),
      honoredSignificants: values.honoredSignificants.map((indi: { email: string }) => indi.email),
    }
    if (post) {
      await postUpdate({
        variables: {
          postId: post.postId,
          post: { honor: honorInfo, content: values.commendation },
        },
      })
      toggle()
    } else {
      await postCreate({
        variables: {
          post: { honor: honorInfo, content: values.commendation, postType: PostType.Honor, sectionId, groupId },
        },
      })
      toggle()
    }
  }

  children = React.cloneElement(children as any, {
    ...props,
    onClick: () => toggle(),
  })

  const formComponent = (
    <div className={s.form}>
      <Form
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ offset: 1 }}
        requiredMark={!post}
        validateMessages={{ required: t('post.modal.createValidation') }}
        initialValues={initialValues}
      >
        <Form.Item name='title' label={t('post.honor.modal.title')} rules={[{ required: true }]}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name='commendation' label={t('post.honor.modal.commendation')} rules={[{ required: true }]}>
          <Input.TextArea allowClear />
        </Form.Item>
        <Form.Item name='honoredProjects' label={t('post.honor.modal.honoredProjects')}>
          <Input.TextArea allowClear />
        </Form.Item>
        <Form.Item name='honoredSignificants' label={t('post.honor.modal.honoredSignificants')}>
          <MultipleUserSearch />
        </Form.Item>
        <Form.Item
          name='honoredIndividuals'
          label={t('post.honor.modal.honoredIndividuals')}
          rules={[{ required: true }]}
        >
          <MultipleUserSearch />
        </Form.Item>
      </Form>
    </div>
  )

  const footer = (
    <div className={s.footer}>
      <Button loading={createLoading || updateLoading} type='primary' onClick={() => onOK()}>
        {post ? t('post.editor.save') : t('post.editor.publishTo')}
      </Button>
      {showGroupSelector && (
        <span className={s.groupSelector} onClick={() => toggleGroupSelectorVisible()}>
          {group ? group.name : t('post.editor.myTimeline')}
        </span>
      )}
    </div>
  )

  const groupSelectorHeader = (
    <Typography.Title level={4}>
      {t('post.editor.choosePublishGroup')}
      <Tooltip title={t('impact.tip.publish')}>
        <BulbOutlined style={{ color: '#FFBB5C', marginLeft: 8, fontSize: 16 }} />
      </Tooltip>
    </Typography.Title>
  )

  const groupSelector = (
    <Modal
      key='groupSelector'
      visible={groupSelectorVisible}
      footer={false}
      className={s.modal}
      onCancel={() => toggleGroupSelectorVisible()}
      title={groupSelectorHeader}
      centered
    >
      <PostEditorSelectorList
        postType={post?.postType}
        setGroupId={setGroupId}
        onSelect={() => toggleGroupSelectorVisible()}
        viewer={viewer}
      />
    </Modal>
  )

  return (
    <>
      {children}
      {groupSelector}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {t('post.honor.modal.header')}
            {sectionId && (
              <>
                <CaretRightOutlined style={{ fontSize: 14, color: '#6A6A6A', marginRight: 4, marginLeft: 4 }} />
                <span
                  className={cls(
                    s.header,
                    sectionId === SectionType.NewAgoran && s.section1Color,
                    sectionId === SectionType.Entertainment && s.section2Color,
                    sectionId === SectionType.Synthesis && s.section3Color
                  )}
                >
                  {sectionId && t(`section.${sectionId}.title`)}
                </span>
              </>
            )}
          </div>
        }
        visible={visible}
        className={s.modal}
        onCancel={() => toggle()}
        footer={footer}
        destroyOnClose
      >
        {formComponent}
      </Modal>
    </>
  )
}
